import { Chip, Text } from '@truepill/react-capsule'
import BreadCrumb from 'components/BreadCrumb'
import CircularChip from 'components/CircularChip'
import IntakeMethodChip from 'components/RxOriginChip'
import TriageOrResolveTriageButton from 'components/TriageOrResolveTriageButton'
import { Eye } from 'react-feather'
import styled from 'styled-components'
import type { Fill, Order, Prescription } from 'types'
import { formatCreatedDateV2 } from 'utils/order'
import ConfirmButton from './ConfirmButton'

type PV1ViewProps = {
  prescription: Prescription
  order: Order
  fill: Fill
}

const RxHeadingV2 = ({ prescription, order, fill }: PV1ViewProps): JSX.Element => {
  if (!order || !fill || !prescription) {
    return <></>
  }

  return (
    <StickyPageHeadingContainer>
      <HeadingInfoContainer>
        <InfoTopRow>
          <BreadCrumb
            useCapsuleDesign={true}
            capsuleCssOverride={{ li: { margin: '0 0 0 0' } }}
            lastCrumbText={`Rx: ${fill.rxFillCode}`}
            navCssOverride={{ paddingLeft: '0' }}
          />
          <IntakeMethodChip origin={prescription.origin} />
          <CircularChip Icon={Eye} />
        </InfoTopRow>
        <InfoBottomRow>
          <OrderLocationChip locationName={order.location?.name} />
          <CustomerNameChip customerName={order.customer?.name} />
          <OrderStatusChip status={order.status} />
          <FillCreatedDateInfo createdAt={fill.createdAt} />
        </InfoBottomRow>
      </HeadingInfoContainer>
      <HeadingButtonContainer>
        <TriageOrResolveTriageButton item={order} fillId={fill._id} forceShowTriage isCapsule={true} />
        <ConfirmButton order={order} fill={fill} />
      </HeadingButtonContainer>
    </StickyPageHeadingContainer>
  )
}

const OrderLocationChip = ({ locationName }: { locationName?: string }): JSX.Element => {
  return (
    <Chip
      css={{
        backgroundColor: 'var(--cap-colors-primary-500)',
        color: 'var(--cap-colors-typography-white)',
        ...ChipCustomStyling,
      }}
    >
      <Text variant="chip">{`${locationName ?? 'Unknown'}`}</Text>
    </Chip>
  )
}

const CustomerNameChip = ({ customerName }: { customerName?: string }): JSX.Element => {
  return (
    <Chip
      css={{
        backgroundColor: 'var(--cap-colors-primary-300)',
        ...ChipCustomStyling,
      }}
      state="info"
    >
      <Text variant="chip">{`${customerName ?? 'Unknown'}`}</Text>
    </Chip>
  )
}

const OrderStatusChip = ({ status }: { status?: string }): JSX.Element => {
  return (
    <Chip
      css={{
        ...ChipCustomStyling,
      }}
      state="warning"
    >
      <Text variant="chip">{`${status ?? 'Unknown'}`}</Text>
    </Chip>
  )
}

const FillCreatedDateInfo = ({ createdAt }: { createdAt?: Date }): JSX.Element => {
  return (
    <div style={{ gap: '0rem' }}>
      <Text variant="caption" bold css={{ color: 'var(--cap-colors-gray-700)', fontSize: '0.9rem' }}>
        Fill created:{' '}
      </Text>
      <Text variant="caption" css={{ color: 'var(--cap-colors-gray-700)', fontSize: '0.9rem' }}>
        {formatCreatedDateV2(createdAt)?.date ?? 'Unknown'}
      </Text>
    </div>
  )
}

const StickyPageHeadingContainer = styled.div`
  position: sticky;
  top: 0em;
  background-color: var(--cap-colors-typography-white);
  z-index: 2;
  padding: 1rem 1.875rem 1.5rem 0rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const HeadingInfoContainer = styled.div`
  flex-grow: 1;
`

const InfoTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`

const InfoBottomRow = styled.div`
  padding: 0.8rem 0rem 0rem 0rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
`

const HeadingButtonContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
`

const ChipCustomStyling = {
  paddingBottom: '0.25rem',
  height: '1.75rem',
}
export default RxHeadingV2
